import { forwardRef, TextareaHTMLAttributes, useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import { Label } from "@/components/base/Label";

type TextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "onChange"
> & {
  id?: string;
  name?: string;
  value: string;
  placeholder?: string;
  className?: string;
  divClassName?: string;
  action?: React.ReactNode;
  actionClassName?: string;
  resizable?: boolean;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
};

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      value,
      placeholder,
      className,
      divClassName,
      action,
      actionClassName,
      resizable = true,
      disabled = false,
      label,
      helperText,
      name,
      id,
      onChange,
      onSubmit,
      ...props
    },
    ref
  ) => {
    const [height, setHeight] = useState("auto");

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const offsetHeight = event.target.offsetHeight;
      const scrollHeight = event.target.scrollHeight;
      if (offsetHeight < scrollHeight && scrollHeight <= 700) {
        setHeight(`${scrollHeight}px`);
      } else if (offsetHeight > scrollHeight && offsetHeight <= 700) {
        setHeight(`${offsetHeight}px`);
      } else if (scrollHeight > 700) {
        setHeight("700px");
      } else if (offsetHeight > 700) {
        setHeight("700px");
      }

      if (event.target.value === "") {
        setHeight("auto");
      }
      onChange(event.target.value);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        event.preventDefault();
        if (onSubmit) {
          onSubmit();
        }
      }
    };

    useEffect(() => {
      if (value === "") {
        setHeight("auto");
      }
    }, [value]);

    return (
      <div
        className={cn(
          "relative grid h-full w-full items-start gap-2",
          divClassName
        )}
      >
        {label && <Label htmlFor={name}>{label}</Label>}
        <textarea
          id={id}
          name={name}
          ref={ref}
          className={cn(
            "placeholder:text-subdued text-text border-lines bg-background focus-visible:ring-ring h-full min-h-full w-full scroll-pb-14 overflow-hidden rounded-md border p-2 pb-14! focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
            !resizable && "resize-none",
            className
          )}
          style={{
            height: height,
            maxHeight: "700px !important",
          }}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          {...props}
        />
        {action && (
          <div
            className={cn(
              "absolute right-3 bottom-3 z-10 flex h-9 flex-row items-center",
              actionClassName
            )}
          >
            {action}
          </div>
        )}
        {helperText && <p className="text-subdued">{helperText}</p>}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };
